import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Loader2, Ruler } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Creates a unit conversion, or edits and resubmits one when the route has an :id.
export default function UnitConversionFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [units, setUnits] = useState([]);
  const [existing, setExisting] = useState(null);
  const [unitCategory, setUnitCategory] = useState('');
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [value, setValue] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/category-conversions/units`)
      .then(res => setUnits(res.data))
      .catch(() => toast.error('Failed to load units'));
  }, []);

  // Edit mode: load the saved submission
  useEffect(() => {
    if (!isEdit) return;
    axios.get(`${API_URL}/unit-conversions/${id}`)
      .then(({ data }) => {
        setExisting(data);
        setFromUnit(data.conversion_from);
        setToUnit(data.conversion_to);
        setValue(String(data.value));
      })
      .catch(() => toast.error('Failed to load unit conversion'));
  }, [id, isEdit]);

  // Edit mode: the unit category is that of the saved From unit, once the units are loaded
  useEffect(() => {
    if (!existing || units.length === 0) return;
    setUnitCategory(units.find(u => u.unit === existing.conversion_from)?.unit_category || '');
  }, [existing, units]);

  const unitCategories = useMemo(() => [...new Set(units.map(u => u.unit_category))].sort(), [units]);
  const unitsInCategory = units.filter(u => u.unit_category === unitCategory).map(u => u.unit);

  const handleCategoryChange = (category) => {
    setUnitCategory(category);
    setFromUnit('');
    setToUnit('');
  };

  const allFieldsFilled = fromUnit && toUnit && value.trim();

  const handleSubmit = async () => {
    const payload = { conversion_from: fromUnit, conversion_to: toUnit, value: Number(value) };
    setSubmitting(true);
    try {
      if (isEdit) {
        await axios.put(`${API_URL}/unit-conversions/${id}`, payload);
        toast.success('Unit conversion resubmitted for approval');
        navigate(`/unit-conversions/${id}`);
      } else {
        await axios.post(`${API_URL}/unit-conversions`, payload);
        toast.success('Unit conversion submitted for approval');
        navigate('/unit-conversions');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error((Array.isArray(detail) ? detail[0].msg : detail) || 'Failed to save unit conversion');
    } finally {
      setSubmitting(false);
    }
  };

  const backUrl = isEdit ? `/unit-conversions/${id}` : '/unit-conversions';

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="unit-conversion-form-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(backUrl)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">
              {isEdit ? `Edit Unit Conversion #${id}` : 'Create Unit Conversion'}
            </h1>
            <p className="text-muted-foreground mt-1">Convert one unit into another within the same unit category. All fields are mandatory.</p>
          </div>
        </div>

        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Ruler className="w-5 h-5" />Unit Conversion</CardTitle>
            <CardDescription>Pick the unit category first, then the two units. The reverse conversion is derived automatically.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label>Unit Category <span className="text-red-500">*</span></Label>
                <Select value={unitCategory} onValueChange={handleCategoryChange}>
                  <SelectTrigger data-testid="unit-category"><SelectValue placeholder="Select unit category" /></SelectTrigger>
                  <SelectContent>
                    {unitCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>From Unit <span className="text-red-500">*</span></Label>
                <Select value={fromUnit} onValueChange={(u) => { setFromUnit(u); if (u === toUnit) setToUnit(''); }} disabled={!unitCategory}>
                  <SelectTrigger data-testid="from-unit"><SelectValue placeholder="Select unit" /></SelectTrigger>
                  <SelectContent>
                    {unitsInCategory.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>To Unit <span className="text-red-500">*</span></Label>
                <Select value={toUnit} onValueChange={setToUnit} disabled={!unitCategory}>
                  <SelectTrigger data-testid="to-unit"><SelectValue placeholder="Select unit" /></SelectTrigger>
                  <SelectContent>
                    {unitsInCategory.filter(u => u !== fromUnit).map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2 max-w-sm">
              <Label htmlFor="value">Value <span className="text-red-500">*</span></Label>
              <Input id="value" type="number" step="any" min="0" value={value} onChange={(e) => setValue(e.target.value)} data-testid="value-input" />
              <p className="text-sm text-muted-foreground">1 {fromUnit || '[from unit]'} = value × {toUnit || '[to unit]'}</p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={() => navigate(backUrl)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !allFieldsFilled} className="btn-primary" data-testid="submit-conversion-btn">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? 'Resubmit for Approval' : 'Submit for Approval'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
